import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

// --- Constantes de backoff ---
const MIN_INTERVAL_MS = 15_000;   // 15s mínimo
const MAX_INTERVAL_MS = 120_000;  // 2 minutos máximo
const BACKOFF_MULTIPLIER = 1.5;   // Fator de crescimento

interface UseLeadsPollingOptions {
    onRefresh: () => void;
    organizationId?: string;
    selectedFunnelId?: string | null;
    /** Quando true, polling considera todas as negociações da org (ex.: dashboard) */
    fetchAllLeads?: boolean;
    enabled?: boolean;
}

/**
 * Faz uma query HEAD (count only) para verificar se o número de leads mudou.
 * Egress: ~100 bytes vs 2-5 KB de um SELECT *.
 */
async function getLeadCount(organizationId?: string, funnelId?: string | null): Promise<number | null> {
    try {
        let query = supabase
            .from('opportunities')
            .select('*', { count: 'exact', head: true });

        if (organizationId) {
            query = query.eq('organization_id', organizationId);
        }

        // Se tem funil, filtra por etapas do funil + ganhos/perdidos
        if (funnelId) {
            const { data: stages } = await supabase
                .from('funnel_stages')
                .select('id')
                .eq('funnel_id', funnelId);

            const stageIds = (stages || []).map(s => s.id);
            // Não misturar negociações entre funis:
            // filtramos somente por `stage ∈ etapas do funil`.
            if (stageIds.length > 0) {
                query = organizationId
                    ? query.eq('organization_id', organizationId).in('stage', stageIds)
                    : query.in('stage', stageIds);
            }
        }

        const { count, error } = await query;
        if (error) return null;
        return count ?? 0;
    } catch {
        return null;
    }
}

export const useLeadsPolling = ({
    onRefresh,
    organizationId,
    selectedFunnelId,
    fetchAllLeads = false,
    enabled = true
}: UseLeadsPollingOptions) => {
    const onRefreshRef = useRef(onRefresh);
    const intervalRef = useRef(MIN_INTERVAL_MS);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastCountRef = useRef<number | null>(null);
    const orgIdRef = useRef(organizationId);
    const funnelIdRef = useRef(selectedFunnelId);
    const fetchAllLeadsRef = useRef(fetchAllLeads);

    // Mantém referências atualizadas
    onRefreshRef.current = onRefresh;
    orgIdRef.current = organizationId;
    funnelIdRef.current = selectedFunnelId;
    fetchAllLeadsRef.current = fetchAllLeads;

    const scheduleNext = useCallback((cancelled: { current: boolean }) => {
        if (cancelled.current) return;

        timerRef.current = setTimeout(async () => {
            if (cancelled.current) return;

            // Não faz polling se a aba está oculta
            if (document.visibilityState !== 'visible') {
                scheduleNext(cancelled);
                return;
            }

            // 1. Faz count leve (HEAD request, ~100 bytes de egress)
            const currentCount = await getLeadCount(
                orgIdRef.current,
                fetchAllLeadsRef.current ? null : funnelIdRef.current
            );

            if (cancelled.current) return;

            // 2. Compara com count anterior
            const hasChanges = currentCount !== null && currentCount !== lastCountRef.current;

            if (hasChanges) {
                // Mudança detectada → busca dados completos + reseta backoff
                lastCountRef.current = currentCount;
                intervalRef.current = MIN_INTERVAL_MS;
                onRefreshRef.current();
            } else {
                // Sem mudança → aumenta backoff
                intervalRef.current = Math.min(
                    intervalRef.current * BACKOFF_MULTIPLIER,
                    MAX_INTERVAL_MS
                );
            }

            // 3. Agenda próximo ciclo
            scheduleNext(cancelled);
        }, intervalRef.current);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const cancelled = { current: false };

        // Reset backoff e count quando organização/funil muda
        intervalRef.current = MIN_INTERVAL_MS;
        lastCountRef.current = null;

        // Inicia cadeia de polling
        scheduleNext(cancelled);

        // Listener para refresh ao voltar para a aba — mas SÓ se houve mudanças reais
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                // Reset backoff para checar rapidamente
                intervalRef.current = MIN_INTERVAL_MS;

                // Faz count-check leve antes de disparar refresh completo
                // Isso evita sobrescrever mudanças locais do usuário
                const currentCount = await getLeadCount(
                    orgIdRef.current,
                    fetchAllLeadsRef.current ? null : funnelIdRef.current
                );
                if (currentCount !== null && currentCount !== lastCountRef.current) {
                    lastCountRef.current = currentCount;
                    onRefreshRef.current(); // Só atualiza se algo mudou no backend
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled.current = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, fetchAllLeads, scheduleNext]);
};
